import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_CHECKUP_LIGHT } from "@/lib/seo-defs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowRight, Loader2, Lightbulb, Target, UserCheck, ShieldCheck, RotateCcw, Info } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Public, no-auth AI mini check-up.
 *
 * Funnel:
 *   1. Visitor describes their challenge (3 fields, ~30 seconds to fill)
 *   2. Claude Sonnet 4.5 returns a tailored mini-analysis (~2-3 seconds)
 *   3. Visitor sees value FIRST, then is offered registration to get the full
 *      curated shortlist of fractional managers.
 *
 * Anti-spam: ONE free analysis per session_token (stored in localStorage).
 * If the visitor tries to redo it, the cached analysis is shown and they're
 * pushed toward registration.
 */

const SECTORS = [
  "Manifatturiero",
  "Edilizia / Costruzioni",
  "Logistica / Trasporti",
  "Retail / Commercio",
  "Food & Beverage",
  "Tessile / Moda",
  "Servizi alle imprese",
  "ICT / Software",
  "Healthcare / Farmaceutico",
  "Energia / Utilities",
  "Agritech / Agroalimentare",
  "Turismo / Hospitality",
  "Finanza / Assicurazioni",
  "Altro",
];

const COMPANY_SIZES = [
  "1-9 dipendenti",
  "10-29 dipendenti",
  "30-49 dipendenti",
  "50-99 dipendenti",
  "100-249 dipendenti",
  "250+ dipendenti",
];

const SESSION_KEY = "cxoora_light_session";
const RESULT_KEY = "cxoora_light_result";

const generateSessionToken = () => {
  // crypto.randomUUID() is widely supported in 2026
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `lc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getOrCreateSessionToken = () => {
  let t = null;
  try {
    t = localStorage.getItem(SESSION_KEY);
  } catch (_) { /* localStorage unavailable */ }
  if (!t) {
    t = generateSessionToken();
    try { localStorage.setItem(SESSION_KEY, t); } catch (_) { /* noop */ }
  }
  return t;
};

const persistResult = (result) => {
  try { localStorage.setItem(RESULT_KEY, JSON.stringify(result)); } catch (_) { /* noop */ }
};

const readPersistedResult = () => {
  try {
    const raw = localStorage.getItem(RESULT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export default function CheckupLightPage() {
  const navigate = useNavigate();
  const sessionToken = useMemo(() => getOrCreateSessionToken(), []);

  const [challenge, setChallenge] = useState("");
  const [sector, setSector] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // On mount, see if this session already has an analysis (cached on backend)
  useEffect(() => {
    let mounted = true;
    const cached = readPersistedResult();
    if (cached) {
      setResult(cached);
      return;
    }
    // No localStorage cache → try server (covers cross-device with same token)
    api.get(`/checkup-light/by-token/${encodeURIComponent(sessionToken)}`)
      .then((res) => {
        if (!mounted || !res.data) return;
        setResult(res.data);
        persistResult(res.data);
      })
      .catch(() => { /* 404 expected on fresh visitor */ });
    return () => { mounted = false; };
  }, [sessionToken]);

  const charsLeft = 500 - challenge.length;
  const canSubmit = challenge.trim().length >= 10 && sector && companySize && !loading;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/checkup-light/", {
        challenge: challenge.trim(),
        sector,
        company_size: companySize,
        session_token: sessionToken,
      });
      setResult(data);
      persistResult(data);
      // Scroll to result smoothly
      setTimeout(() => {
        document.getElementById("light-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore imprevisto. Riprova tra qualche istante.";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Anti-spam C: cannot do a SECOND free analysis. Push toward registration.
    toast.info("Hai già la tua analisi gratuita. Registrati per fare il check-up completo e ricevere una shortlist di manager.");
    navigate("/register");
  };

  const hasResult = !!result?.diagnosis;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="checkup-light-page">
      <SEO {...SEO_CHECKUP_LIGHT} />
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1730] via-[#0B1D40] to-[#071225] text-white">
        <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#2D6BFF] blur-3xl opacity-30" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#7AA0FF] blur-3xl opacity-20" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/15">
            <Sparkles size={12} className="mr-1.5" /> Analisi gratuita in 60 secondi
          </Badge>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15]">
            Descrivi la tua sfida.<br />
            <span className="text-[#7AA0FF]">L'AI ti dà subito una prima diagnosi.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">
            Niente registrazione, niente impegno. In 3 campi descrivi il problema della tua impresa
            e ricevi un'analisi consulenziale generata da AI con 3 azioni concrete e il profilo di
            manager che può aiutarti.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-xs text-white/60">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-[#7AA0FF]" /> Dati anonimi finché non ti registri
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={14} className="text-[#7AA0FF]" /> Powered by Claude Sonnet 4.5
            </span>
          </div>
        </div>
      </section>

      {/* Form + Result */}
      <section className="flex-1 mx-auto max-w-4xl w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {!hasResult && (
          <Card className="p-6 sm:p-8 border-slate-200" data-testid="light-form-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="light-challenge" className="text-sm font-semibold text-slate-900">
                  Qual è la sfida principale della tua azienda? <span className="text-red-600">*</span>
                </Label>
                <p className="text-xs text-slate-500">
                  Descrivi liberamente. Più sei specifico/a, più precisa sarà l'analisi.
                  (es. "Stiamo crescendo come fatturato ma i margini calano e non capisco dove perdo soldi")
                </p>
                <Textarea
                  id="light-challenge"
                  data-testid="light-challenge-input"
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value.slice(0, 500))}
                  rows={5}
                  placeholder="Scrivi qui la tua sfida..."
                  className="resize-none"
                  required
                  minLength={10}
                  maxLength={500}
                />
                <div className="flex justify-between text-xs">
                  <span className={challenge.length < 10 ? "text-amber-600" : "text-slate-500"}>
                    {challenge.length < 10 ? `Minimo 10 caratteri (${10 - challenge.length} mancanti)` : "Pronto"}
                  </span>
                  <span className="text-slate-400">{charsLeft} caratteri rimanenti</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-900">
                    Settore <span className="text-red-600">*</span>
                  </Label>
                  <Select value={sector} onValueChange={setSector}>
                    <SelectTrigger data-testid="light-sector-select" className="w-full">
                      <SelectValue placeholder="Seleziona settore" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-900">
                    Dimensione azienda <span className="text-red-600">*</span>
                  </Label>
                  <Select value={companySize} onValueChange={setCompanySize}>
                    <SelectTrigger data-testid="light-size-select" className="w-full">
                      <SelectValue placeholder="Seleziona dimensione" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="light-error">
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  type="submit"
                  size="lg"
                  disabled={!canSubmit}
                  className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white shadow-lg shadow-blue-900/20 h-12 px-6 text-base font-semibold flex-1"
                  data-testid="light-submit-btn"
                >
                  {loading ? (
                    <><Loader2 size={18} className="animate-spin mr-2" /> Analisi in corso...</>
                  ) : (
                    <><Sparkles size={18} className="mr-2" /> Genera l'analisi AI</>
                  )}
                </Button>
              </div>

              <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                <Info size={14} className="shrink-0 mt-0.5 text-slate-500" />
                <span>
                  L'analisi è generata da AI e ha valore <strong>orientativo</strong>. Per ricevere
                  una shortlist verificata di manager reali ti chiederemo di registrarti.
                </span>
              </div>
            </form>
          </Card>
        )}

        {/* Result */}
        {hasResult && (
          <div id="light-result" className="space-y-6" data-testid="light-result-card">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-[#2D6BFF]" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">La tua analisi</h2>
            </div>

            {/* Diagnosis */}
            <Card className="p-6 sm:p-8 border-slate-200">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#EAF0FF] text-[#2D6BFF] flex items-center justify-center shrink-0">
                  <Lightbulb size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Diagnosi</h3>
                  <p className="text-xs text-slate-500">Cosa abbiamo capito dalla tua descrizione</p>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed" data-testid="light-diagnosis">
                {result.diagnosis}
              </p>
            </Card>

            {/* Actions */}
            <Card className="p-6 sm:p-8 border-slate-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#EAF0FF] text-[#2D6BFF] flex items-center justify-center shrink-0">
                  <Target size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">3 azioni concrete</h3>
                  <p className="text-xs text-slate-500">Attuabili nei prossimi 30 giorni</p>
                </div>
              </div>
              <ol className="space-y-3" data-testid="light-actions">
                {(result.actions || []).map((action, i) => (
                  <li key={i} className="flex gap-3" data-testid={`light-action-${i}`}>
                    <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#2D6BFF] text-white text-sm font-bold cxo-tabular">
                      {i + 1}
                    </span>
                    <span className="text-slate-700 leading-relaxed pt-0.5">{action}</span>
                  </li>
                ))}
              </ol>
            </Card>

            {/* Recommended manager */}
            <Card className="p-6 sm:p-8 border-[#2D6BFF]/30 bg-gradient-to-br from-[#EAF0FF]/40 to-white">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D6BFF] text-white flex items-center justify-center shrink-0">
                  <UserCheck size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">Il manager che ti serve</h3>
                  <p className="text-xs text-slate-500">Sulla base della tua sfida, il profilo più adatto è:</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge className="bg-[#2D6BFF] text-white hover:bg-[#1F57F2] text-base px-4 py-1.5 font-bold" data-testid="light-manager-role">
                  {result.recommended_manager_role}
                </Badge>
              </div>
            </Card>

            {/* Conversion CTA */}
            <Card className="p-6 sm:p-8 border-slate-900 bg-slate-900 text-white" data-testid="light-cta-card">
              <h3 className="font-display text-xl sm:text-2xl font-bold">
                Pronto/a a candidare il tuo problema ai manager reali?
              </h3>
              <p className="mt-2 text-sm sm:text-base text-white/80 leading-relaxed">
                Registrati per completare il check-up dettagliato (7 step, 5 minuti) — l'analisi
                che hai appena letto sarà già pre-compilata. Ti invieremo una shortlist di
                3-5 manager fractional verificati con esperienza diretta sulla tua sfida.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="flex-1">
                  <Button
                    size="lg"
                    className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white shadow-lg shadow-blue-950/40 h-12 px-6 text-base font-semibold w-full"
                    data-testid="light-register-cta"
                  >
                    Registrati e completa il check-up
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleReset}
                  className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white h-12 px-6 text-base font-medium"
                  data-testid="light-restart-btn"
                  title="L'analisi gratuita è una sola. Per rifarla, registrati."
                >
                  <RotateCcw size={16} className="mr-2" /> Nuova analisi
                </Button>
              </div>
              <p className="mt-3 text-xs text-white/60">
                100% gratis · Nessun obbligo · I tuoi dati restano riservati (GDPR-compliant)
              </p>
            </Card>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
